import type { Comparator } from '@domain/schemas/filters.schema';
import { ALL_CLASSES_OF_BUSINESS } from '@root/helpers/constants';
import clsx from 'clsx';

import type { FieldDef } from './types';

// Local override for the shadcn combobox focus state.
export const APP_FOCUS_RING_OVERRIDE = clsx(
  'has-[[data-slot=input-group-control]:focus-visible]:ring-0',
  'has-[[data-slot=input-group-control]:focus-visible]:border-info-300',
  'has-[[data-slot=input-group-control]:focus-visible]:outline',
  'has-[[data-slot=input-group-control]:focus-visible]:outline-2',
  'has-[[data-slot=input-group-control]:focus-visible]:outline-primary-500',
);

export const IGNORE_IDS = [
  'risk_details.period.0.period_matrix.from.time',
  'risk_details.period.0.period_matrix.from.timezone',
  'risk_details.period.0.period_matrix.to.time',
  'risk_details.period.0.period_matrix.to.timezone',
];

export const ALL_COMPARATORS: Comparator[] = ['eq', 'neq', 'contains', 'gt', 'lt'];

export const COMPARATOR_LABELS: Record<Comparator, string> = {
  none: 'NONE',
  eq: 'EQUALS',
  neq: 'NOT EQUALS',
  contains: 'CONTAINS',
  gt: 'GREATER THAN',
  lt: 'LESS THAN',
  ex: 'IS NOT EMPTY',
  nex: 'IS EMPTY',
  rel: 'RELATIVE',
};

export const TEXT_COMPARATORS: Comparator[] = ['eq', 'neq', 'contains'];
const ENUM_COMPARATORS: Comparator[] = ['eq', 'neq'];
const RANGE_COMPARATORS: Comparator[] = ['eq', 'neq', 'gt', 'lt', 'rel'];

const BLOCK_TYPES = [
  { label: 'Clause Fullwidth', value: 'clause' },
  { label: 'Block Fullwidth', value: 'generic' },
  { label: 'Block Heading', value: 'mrc-heading' },
  { label: 'Clause Heading', value: 'clause-heading' },
];

const CLAUSE_TYPES = [
  { label: 'Condition', value: 'Condition' },
  { label: 'Endorsement', value: 'Endorsement' },
  { label: 'Exclusion', value: 'Exclusion' },
  { label: 'Express Warranties', value: 'Express Warranties' },
  { label: 'Standard Warranties', value: 'Standard Warranties' },
  { label: 'Wording', value: 'Wording' },
];

const CLAUSE_CLASSIFICATIONS = [
  { label: 'Standard', value: 'Standard' },
  { label: 'Non-Standard', value: 'Non-Standard' },
  { label: 'LMW', value: 'LMW' },
];

export const BLOCK_CLAUSE_FIELD_DEFS: FieldDef[] = [
  {
    id: 'blocks.name',
    label: 'Block Name',
    comparators: TEXT_COMPARATORS,
    valueInput: 'TextInput',
  },
  {
    id: 'blocks.title',
    label: 'Block Title',
    comparators: TEXT_COMPARATORS,
    valueInput: 'TextInput',
  },
  {
    id: 'blocks.type',
    label: 'Block Layout',
    comparators: ENUM_COMPARATORS,
    valueInput: 'LookupMultiSelect',
    options: BLOCK_TYPES,
  },
  {
    id: 'blocks.classification',
    label: 'Clause Classification',
    comparators: ENUM_COMPARATORS,
    valueInput: 'LookupMultiSelect',
    options: CLAUSE_CLASSIFICATIONS,
  },
  {
    id: 'blocks.clause_type',
    label: 'Clause Type',
    comparators: ENUM_COMPARATORS,
    valueInput: 'LookupMultiSelect',
    options: CLAUSE_TYPES,
  },
  {
    id: 'blocks.clause_reference',
    label: 'Clause Reference',
    comparators: TEXT_COMPARATORS,
    valueInput: 'TextInput',
  },
];

export const CONTRACT_DETAILS_FIELD_DEFS: FieldDef[] = [
  {
    id: 'ref',
    label: 'Contract ID',
    comparators: TEXT_COMPARATORS,
    valueInput: 'TextInput',
  },
  {
    id: 'name',
    label: 'Contract Name',
    comparators: TEXT_COMPARATORS,
    valueInput: 'TextInput',
  },
  {
    id: 'external_id',
    label: 'External ID',
    comparators: [...TEXT_COMPARATORS, 'nex', 'ex'],
    valueInput: 'TextInput',
  },
  {
    id: 'teams',
    label: 'Team',
    comparators: ENUM_COMPARATORS,
    valueInput: 'LookupMultiSelect',
  },
  {
    id: 'status',
    label: 'Status',
    comparators: ENUM_COMPARATORS,
    valueInput: 'EnumMultiSelect',
  },
  {
    id: 'contract_missing_data_count',
    label: 'Contract Validation',
    comparators: ['eq', 'neq', 'gt', 'lt'],
    valueInput: 'NumberInput',
  },
  {
    id: 'unresolvedCommentsCount',
    label: 'Comments',
    comparators: ['eq', 'neq', 'gt', 'lt'],
    valueInput: 'NumberInput',
  },
  {
    // Absent until a guidance scan has completed, so existence is the switch.
    id: 'guidanceFindingsCount',
    label: 'Contract Guidance',
    comparators: ['none', 'ex', 'nex'],
    valueInput: 'FlagSelect',
    flagOptions: [
      { label: 'Has guidance run', value: 'show' },
      { label: 'No guidance run', value: 'hide' },
    ],
  },
  {
    id: 'endorsementsCount',
    label: 'Endorsements',
    comparators: ['eq', 'gt', 'lt'],
    valueInput: 'NumberInput',
  },
  {
    id: 'renewalsCount',
    label: 'Renewals',
    comparators: ['eq', 'gt', 'lt'],
    valueInput: 'NumberInput',
  },
  {
    id: 'tags',
    label: 'Tags',
    comparators: ENUM_COMPARATORS,
    valueInput: 'LookupMultiSelect',
  },
  {
    id: 'renewalParentId',
    label: 'Contract Type',
    comparators: ['none', 'ex', 'nex'],
    valueInput: 'FlagSelect',
    flagOptions: [
      { label: 'Renewal', value: 'show' },
      { label: 'Original', value: 'hide' },
    ],
  },
  {
    id: 'last_updated_by',
    label: 'Updated By',
    comparators: ENUM_COMPARATORS,
    valueInput: 'LookupMultiSelect',
  },
  {
    id: 'updated_at',
    label: 'Updated At',
    comparators: RANGE_COMPARATORS,
    valueInput: 'DateInput',
  },
  {
    id: 'created_by',
    label: 'Created By',
    comparators: ENUM_COMPARATORS,
    valueInput: 'LookupMultiSelect',
  },
  {
    id: 'created_at',
    label: 'Created At',
    comparators: RANGE_COMPARATORS,
    valueInput: 'DateInput',
  },
  {
    id: 'branding',
    label: 'Branding',
    comparators: ENUM_COMPARATORS,
    valueInput: 'LookupMultiSelect',
  },
  {
    id: 'class_of_business',
    label: 'Class of Business',
    comparators: ENUM_COMPARATORS,
    valueInput: 'LookupMultiSelect',
    options: ALL_CLASSES_OF_BUSINESS.map((value) => ({ label: value, value })),
  },
  {
    id: 'sourceResourceDisplayName',
    label: 'Template',
    comparators: ENUM_COMPARATORS,
    valueInput: 'LookupMultiSelect',
  },
  {
    id: 'sourceResourceName',
    label: 'Base',
    comparators: ENUM_COMPARATORS,
    valueInput: 'LookupMultiSelect',
    options: [
      { label: 'Contract', value: 'submissions' },
      { label: 'Template', value: 'templates' },
    ],
  },
];

export const FIELD_DEF_MAP = new Map<string, FieldDef>(
  [...CONTRACT_DETAILS_FIELD_DEFS, ...BLOCK_CLAUSE_FIELD_DEFS].map((f) => [f.id, f]),
);

export const SORTED_CONTRACT_DETAILS_FIELD_DEFS = [...CONTRACT_DETAILS_FIELD_DEFS].sort((a, b) =>
  a.label.localeCompare(b.label),
);

export const EMPTY_OPTIONS_RECORD: Record<string, never[]> = {};
